/**
 * <site-header> custom element.
 *
 * Behavior:
 *  - Opens/closes mobile menu drawer via <dialog>
 *  - Toggles a data-scrolled attribute when the page scrolls past a threshold
 *    so the sticky header can grow a shadow
 *  - Listens for the global "cart:updated" event to refresh the cart count badge
 */
class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[data-drawer]');
    this.menuToggle = this.querySelector('[data-action="menu-toggle"]');
    this.menuClose = this.querySelector('[data-action="menu-close"]');
    this.cartButton = this.querySelector('[data-action="cart-open"]');
    this.cartCount = this.querySelector('[data-cart-count]');

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.openMenu());
    }
    if (this.menuClose) {
      this.menuClose.addEventListener('click', () => this.closeMenu());
    }
    if (this.drawer) {
      this.drawer.addEventListener('close', () => {
        if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'false');
      });
    }
    // Cart opener is handled by <medcline-cart-drawer> via document-level [data-action="cart-open"] delegation.

    this._onScroll = this._onScroll.bind(this);
    if (this.classList.contains('site-header--sticky')) {
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    document.addEventListener('cart:updated', (e) => this.updateCount(e.detail?.item_count));
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
  }

  openMenu() {
    if (!this.drawer) return;
    this.drawer.showModal();
    if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    if (!this.drawer) return;
    this.drawer.close();
    if (this.menuToggle) this.menuToggle.setAttribute('aria-expanded', 'false');
  }

  _onScroll() {
    const scrolled = window.scrollY > 4;
    this.setAttribute('data-scrolled', scrolled ? 'true' : 'false');
  }

  updateCount(count) {
    if (!this.cartCount || typeof count !== 'number') return;
    this.cartCount.textContent = count;
    if (count > 0) this.cartCount.removeAttribute('hidden');
    else this.cartCount.setAttribute('hidden', '');
  }
}

if (!customElements.get('site-header')) {
  customElements.define('site-header', SiteHeader);
}

class MedClineMegaMenu extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-mega-trigger]')];
    this.openTimer = null;
    this.closeTimer = null;
    this.activeTrigger = null;

    this.items.forEach((trigger) => {
      const item = trigger.closest('.site-header__nav-item');
      const panel = item?.querySelector('[data-mega-panel]');
      if (!panel) return;

      trigger.addEventListener('click', (e) => {
        // Parent links navigate by default. Only intercept on the chevron
        // (or when the trigger has no real href) to toggle the dropdown.
        const href = trigger.getAttribute('href');
        const isChevron = e.target.closest('.site-header__nav-chev');
        const noNav = !href || href === '#' || href === '';
        if (isChevron || noNav) {
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.closeAll();
          if (!isOpen) this.open(trigger, panel);
        }
      });
      trigger.addEventListener('mouseenter', () => this.scheduleOpen(trigger, panel));
      // Pointer clicks focus links before `click` fires. Only auto-open for
      // keyboard focus so the subsequent chevron click does not close a panel
      // that the focus handler opened a moment earlier.
      trigger.addEventListener('focus', () => {
        if (trigger.matches(':focus-visible')) this.open(trigger, panel);
      });
      item.addEventListener('mouseleave', () => this.scheduleClose());
      panel.addEventListener('mouseenter', () => this.cancelClose());
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    });

    this._onDocClick = (e) => {
      if (!this.contains(e.target)) this.closeAll();
    };
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.activeTrigger) {
        this.activeTrigger.focus();
        this.closeAll();
      }
    };
    document.addEventListener('click', this._onDocClick);
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey);
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  scheduleOpen(trigger, panel) {
    this.cancelClose();
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(trigger, panel), 120);
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.closeAll(), 180);
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
  }

  open(trigger, panel) {
    if (this.activeTrigger && this.activeTrigger !== trigger) this.closeAll();
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.activeTrigger = trigger;
  }

  closeAll() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.items.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const panel = trigger.closest('.site-header__nav-item')?.querySelector('[data-mega-panel]');
      if (panel) panel.hidden = true;
    });
    this.activeTrigger = null;
  }
}

if (!customElements.get('medcline-mega-menu')) {
  customElements.define('medcline-mega-menu', MedClineMegaMenu);
}

// Must match the .is-entering / .is-leaving animation-duration in section
// header.liquid's {% style %} block.
const PROMO_TRANSITION_MS = 440;

/**
 * <promo-bar> custom element.
 *
 * Rotates through [data-promo-slide] children in a continuous loop.
 * - Does nothing when there's only one slide (stays static).
 * - Pauses on hover/focus so a keyboard or mouse user can read/activate a link
 *   before it rotates away.
 * - Honors prefers-reduced-motion by never starting the rotation timer.
 */
class PromoBar extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-promo-slide]')];
    this.index = 0;
    this.timer = null;
    this.transitionTimer = null;
    if (this.slides.length <= 1) return;

    this.interval = parseInt(this.dataset.interval, 10) || 5000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());

    this.play();
  }

  disconnectedCallback() {
    this.pause();
    clearTimeout(this.transitionTimer);
  }

  play() {
    if (this.reduceMotion || this.slides.length <= 1) return;
    this.pause();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  next() {
    const current = this.slides[this.index];
    this.index = (this.index + 1) % this.slides.length;
    const upcoming = this.slides[this.index];

    current.classList.add('is-leaving');
    upcoming.hidden = false;
    upcoming.classList.add('is-entering');

    clearTimeout(this.transitionTimer);
    this.transitionTimer = setTimeout(() => {
      current.hidden = true;
      current.classList.remove('is-leaving');
      upcoming.classList.remove('is-entering');
    }, PROMO_TRANSITION_MS);
  }
}

if (!customElements.get('promo-bar')) {
  customElements.define('promo-bar', PromoBar);
}
